import type { ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type { ActivationLink } from '@toolproof-core/visualization/_contract/item-activation';
import type { ConstellationKey, ItemKey } from '@toolproof-core/visualization/_contract/key';

import {
    WORKFLOW_KEYS,
    buildCaseConstellationKey,
    buildRoleConstellationKey,
    buildToolItemKey,
    buildToolStepItemKey,
    buildWorkflowStateEntryConstellationKey,
    parseRoleCollectionKey,
    parseRoleItemKey,
    parseWorkflowStateEntryCollectionKey,
    parseWorkflowStateEntryItemKey,
    type WorkflowKeyScope,
} from '@/workflow/_pure/keyCodec';
import type { CaseMetadata } from '@/workflow/_contract/kernel';

export { buildToolStepItemKey } from '@/workflow/_pure/keyCodec';

export type SharedCaseActivationMetadata = {
    ownerMacroStepKey: string;
    whenToolStepKey: string;
    whatToolStepKey: string;
};

export function appendActivationLink(args: {
    links: ActivationLink[];
    sourceConstellationKey: ActivationLink['sourceConstellationKey'];
    targetConstellationKey: ActivationLink['targetConstellationKey'];
    sourceItemKey: ItemKey<'entity'>;
    targetItemKey: ItemKey<'entity'>;
}) {
    const {
        links,
        sourceConstellationKey,
        targetConstellationKey,
        sourceItemKey,
        targetItemKey,
    } = args;

    let link = links.find((candidate) => (
        candidate.sourceConstellationKey === sourceConstellationKey
        && candidate.targetConstellationKey === targetConstellationKey
    ));

    if (!link) {
        link = {
            sourceConstellationKey,
            targetConstellationKey,
            targetItemKeysBySourceItemKey: {},
        };
        links.push(link);
    }

    const targetItemKeys = link.targetItemKeysBySourceItemKey[sourceItemKey] ?? [];
    if (!targetItemKeys.includes(targetItemKey)) {
        targetItemKeys.push(targetItemKey);
    }
    link.targetItemKeysBySourceItemKey[sourceItemKey] = targetItemKeys;
}

export function appendCaseMembershipActivationLinks(args: {
    links: ActivationLink[];
    memberships: readonly CaseMetadata[];
    toolStepConstellationKey: ConstellationKey<'entity'>;
}): void {
    for (const membership of args.memberships) {
        const when = buildToolStepItemKey(membership.whenToolStepKey);
        const what = buildToolStepItemKey(membership.whatToolStepKey);
        for (const [sourceItemKey, targetItemKey] of [[when, what], [what, when]]) {
            appendActivationLink({
                links: args.links,
                sourceConstellationKey: args.toolStepConstellationKey,
                targetConstellationKey: args.toolStepConstellationKey,
                sourceItemKey,
                targetItemKey,
            });
        }
    }
}

export function deriveSharedActivationLinks(args: {
    itemCollections?: ItemCollection[];
    toolStepConstellationKey: ConstellationKey<'entity'>;
    orderedToolSteps: Array<{
        stepKey: string;
        toolHandle: ToolHandle;
    }>;
    activeToolStep?: {
        toolHandle: ToolHandle;
        stepKey: string;
    };
    caseMetadataByItemKey?: ReadonlyMap<ItemKey<'entity'>, SharedCaseActivationMetadata>;
    includeRoleWorkflowStateEntryLinks?: boolean;
    workflowKeyScope?: WorkflowKeyScope;
    caseMemberships?: readonly CaseMetadata[];
}): ActivationLink[] {
    const links: ActivationLink[] = [];
    appendCaseMembershipActivationLinks({
        links,
        memberships: args.caseMemberships ?? [],
        toolStepConstellationKey: args.toolStepConstellationKey,
    });
    const toolStepItemKeysByToolHandle = new Map<ToolHandle, Set<ItemKey<'entity'>>>();

    for (const toolStep of args.orderedToolSteps) {
        const toolStepItemKey = buildToolStepItemKey(toolStep.stepKey);
        let toolStepKeySet = toolStepItemKeysByToolHandle.get(toolStep.toolHandle);
        if (!toolStepKeySet) {
            toolStepKeySet = new Set<ItemKey<'entity'>>();
            toolStepItemKeysByToolHandle.set(toolStep.toolHandle, toolStepKeySet);
        }
        toolStepKeySet.add(toolStepItemKey);

        appendActivationLink({
            links,
            sourceConstellationKey: args.toolStepConstellationKey,
            targetConstellationKey: WORKFLOW_KEYS.constellations.tool,
            sourceItemKey: toolStepItemKey,
            targetItemKey: buildToolItemKey(toolStep.toolHandle),
        });
    }

    if (args.activeToolStep) {
        let toolStepKeySet = toolStepItemKeysByToolHandle.get(args.activeToolStep.toolHandle);
        if (!toolStepKeySet) {
            toolStepKeySet = new Set<ItemKey<'entity'>>();
            toolStepItemKeysByToolHandle.set(args.activeToolStep.toolHandle, toolStepKeySet);
        }
        toolStepKeySet.add(buildToolStepItemKey(args.activeToolStep.stepKey));
    }

    for (const [toolHandle, toolStepItemKeySet] of toolStepItemKeysByToolHandle.entries()) {
        const sourceToolItemKey = buildToolItemKey(toolHandle);
        for (const toolStepItemKey of toolStepItemKeySet) {
            appendActivationLink({
                links,
                sourceConstellationKey: WORKFLOW_KEYS.constellations.tool,
                targetConstellationKey: args.toolStepConstellationKey,
                sourceItemKey: sourceToolItemKey,
                targetItemKey: toolStepItemKey,
            });
        }
    }

    if (args.caseMetadataByItemKey && args.caseMetadataByItemKey.size > 0 && args.workflowKeyScope) {
        for (const [caseItemKey, metadata] of args.caseMetadataByItemKey.entries()) {
            const caseConstellationKey = buildCaseConstellationKey({
                ...args.workflowKeyScope,
                ownerMacroStepKey: metadata.ownerMacroStepKey,
            });
            const whenToolStepItemKey = buildToolStepItemKey(metadata.whenToolStepKey);
            const whatToolStepItemKey = buildToolStepItemKey(metadata.whatToolStepKey);

            appendActivationLink({
                links,
                sourceConstellationKey: caseConstellationKey,
                targetConstellationKey: args.toolStepConstellationKey,
                sourceItemKey: caseItemKey,
                targetItemKey: whenToolStepItemKey,
            });
            appendActivationLink({
                links,
                sourceConstellationKey: caseConstellationKey,
                targetConstellationKey: args.toolStepConstellationKey,
                sourceItemKey: caseItemKey,
                targetItemKey: whatToolStepItemKey,
            });

            appendActivationLink({
                links,
                sourceConstellationKey: args.toolStepConstellationKey,
                targetConstellationKey: caseConstellationKey,
                sourceItemKey: whenToolStepItemKey,
                targetItemKey: caseItemKey,
            });
            appendActivationLink({
                links,
                sourceConstellationKey: args.toolStepConstellationKey,
                targetConstellationKey: caseConstellationKey,
                sourceItemKey: whatToolStepItemKey,
                targetItemKey: caseItemKey,
            });
        }
    }

    const shouldIncludeRoleWorkflowStateEntryLinks =
        args.includeRoleWorkflowStateEntryLinks !== false;
    if (!shouldIncludeRoleWorkflowStateEntryLinks || !args.activeToolStep || !args.itemCollections) {
        return links;
    }

    type RoleIdentity = `${'input' | 'output'}:${string}:${string}`;
    const roleByIdentity = new Map<RoleIdentity, {
        constellationKey: ReturnType<typeof buildRoleConstellationKey>;
        itemKey: ItemKey<'entity'>;
    }>();

    const workflowStateEntryByIdentity = new Map<RoleIdentity, {
        constellationKey: ReturnType<typeof buildWorkflowStateEntryConstellationKey>;
        itemKey: ItemKey<'entity'>;
    }>();

    for (const itemCollection of args.itemCollections) {
        if (itemCollection.itemCollectionKind !== 'entity') continue;

        const roleCollection = parseRoleCollectionKey(itemCollection.itemCollectionKey);
        if (roleCollection) {
            if (roleCollection.toolHandle !== args.activeToolStep.toolHandle) continue;

            const roleConstellationKey = buildRoleConstellationKey({
                toolHandle: roleCollection.toolHandle,
                direction: roleCollection.direction,
                resourceTypeHandle: roleCollection.resourceTypeHandle,
            });

            for (const roleItem of itemCollection.items) {
                const parsedRoleItem = parseRoleItemKey(roleItem.itemKey);
                if (!parsedRoleItem) continue;

                const roleIdentity = `${parsedRoleItem.direction}:${parsedRoleItem.resourceTypeHandle}:${parsedRoleItem.roleName}` as RoleIdentity;
                roleByIdentity.set(roleIdentity, {
                    constellationKey: roleConstellationKey,
                    itemKey: roleItem.itemKey,
                });
            }

            continue;
        }

        const workflowStateEntryCollection = parseWorkflowStateEntryCollectionKey(itemCollection.itemCollectionKey);
        if (!workflowStateEntryCollection) continue;

        const workflowStateEntryConstellationKey = buildWorkflowStateEntryConstellationKey({
            workflowHandle: workflowStateEntryCollection.workflowHandle,
            threadIndex: workflowStateEntryCollection.threadIndex,
            stepKey: workflowStateEntryCollection.stepKey,
            resourceTypeHandle: workflowStateEntryCollection.resourceTypeHandle,
            direction: workflowStateEntryCollection.direction,
        });

        for (const workflowStateEntryItem of itemCollection.items) {
            const parsedWorkflowStateEntryItem = parseWorkflowStateEntryItemKey(workflowStateEntryItem.itemKey);
            if (!parsedWorkflowStateEntryItem || parsedWorkflowStateEntryItem.stepKey !== args.activeToolStep.stepKey) {
                continue;
            }

            const roleIdentity = `${parsedWorkflowStateEntryItem.direction}:${parsedWorkflowStateEntryItem.resourceTypeHandle}:${parsedWorkflowStateEntryItem.roleName}` as RoleIdentity;
            workflowStateEntryByIdentity.set(roleIdentity, {
                constellationKey: workflowStateEntryConstellationKey,
                itemKey: workflowStateEntryItem.itemKey,
            });
        }
    }

    for (const [identity, roleRef] of roleByIdentity.entries()) {
        const workflowStateEntryRef = workflowStateEntryByIdentity.get(identity);
        if (!workflowStateEntryRef) continue;

        appendActivationLink({
            links,
            sourceConstellationKey: roleRef.constellationKey,
            targetConstellationKey: workflowStateEntryRef.constellationKey,
            sourceItemKey: roleRef.itemKey,
            targetItemKey: workflowStateEntryRef.itemKey,
        });

        appendActivationLink({
            links,
            sourceConstellationKey: workflowStateEntryRef.constellationKey,
            targetConstellationKey: roleRef.constellationKey,
            sourceItemKey: workflowStateEntryRef.itemKey,
            targetItemKey: roleRef.itemKey,
        });
    }

    return links;
}
